import { FunctionComponent, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import Button from '../../../core/components/Button';
import Field from '../../../core/components/Field';
import FilterButton from '../../../core/components/FilterButton';
import Label from '../../../core/components/Label';
import SearchBar from '../../../core/components/SearchBar';
import Select from '../../../core/components/Select';
import {
    CONVERSATION_SKILLS_OPTIONS,
    GENDER_ITEMS,
    LANGUAGE_LEVEL_ITEMS,
    VISA_STATUS_ITEMS
} from '../../constants/options';
import { MOBILE_DEVICE } from '../../../core/constants/styles';

type SearchPanelProps = {
    keyword?: string;
    gender?: string;
    visaStatus?: string;
    conversationSkills?: string;
    jlptLevel?: string;
    placeholder?: string;
    onKeywordChange?: (keyword: string) => void;
    onGenderChange?: (gender: string) => void;
    onVisaStatusChange?: (visaStatus: string) => void;
    onConversationSkillsChange?: (conversationSkills: string) => void;
    onJlptLevelChange?: (jlptLevel: string) => void;
    onSearch?: () => void;
};

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  padding: 20px 0;
  background: #FFFFFF;
  border-bottom: 1px solid #E5E5E5;

  @media ${MOBILE_DEVICE} {
    padding: 20px;
  }
`;

const SearchContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;

  @media ${MOBILE_DEVICE} {
    width: 100%;
  }
`;

const FilterContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: flex-end;
  margin-top: 20px;

  > *:not(:first-child) {
    margin-left: 20px;
  }
`;

const ActionContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  margin-top: 30px;
  
  > *:not(:first-child) {
    margin-left: 60px;
  }
`;

const LocalSearchBar = styled(SearchBar)`
  width: 400px;
`;

const SearchPanel: FunctionComponent<SearchPanelProps> = ({
    keyword,
    gender,
    visaStatus,
    conversationSkills,
    jlptLevel,
    placeholder,
    onKeywordChange,
    onGenderChange,
    onVisaStatusChange,
    onConversationSkillsChange,
    onJlptLevelChange,
    onSearch,
    ...props
}) => {
    const [expanded, setExpanded] = useState(false);
    const { t } = useTranslation();

    return (
        <Container {...props}>
            <SearchContainer>
                <LocalSearchBar onChange={onKeywordChange} onSearch={onSearch} placeholder={placeholder}/>
                <FilterButton
                    style={{ display: 'none', marginLeft: '36px' }}
                    defaultChecked={expanded}
                    onToggle={() => setExpanded(!expanded)}
                />
            </SearchContainer>
            {expanded && (
                <>
                    <FilterContainer>
                        <Field>
                            <Label>{t('tokuty.gender')}</Label>
                            <Select
                                placeholder="core.select"
                                options={GENDER_ITEMS}
                                value={gender}
                                onValueChange={onGenderChange}
                            />
                        </Field>
                        <Field>
                            <Label>{t('tokuty.candidate_visa_status')}</Label>
                            <Select
                                placeholder="core.select"
                                options={VISA_STATUS_ITEMS}
                                value={visaStatus}
                                onValueChange={onVisaStatusChange}
                            />
                        </Field>
                        <Field>
                            <Label>{t('tokuty.japanese_conversation_skills')}</Label>
                            <Select
                                placeholder="core.select"
                                options={CONVERSATION_SKILLS_OPTIONS}
                                value={conversationSkills}
                                onValueChange={onConversationSkillsChange}
                            />
                        </Field>
                        <Field>
                            <Label>{t('tokuty.jlpt_level')}</Label>
                            <Select
                                placeholder="core.select"
                                options={LANGUAGE_LEVEL_ITEMS}
                                value={jlptLevel}
                                onValueChange={onJlptLevelChange}
                            />
                        </Field>
                    </FilterContainer>
                    <ActionContainer>
                        <Button variant="secondary" onClick={onSearch}>
                            {t('tokuty.apply_filter')}
                        </Button>
                        <Button variant="tertiary">{t('tokuty.clear_filter')}</Button>
                        <Button variant="tertiary">{t('tokuty.save_filter')}</Button>
                    </ActionContainer>
                </>
            )}
        </Container>
    );
};

export default SearchPanel;